import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useInView } from 'framer-motion';

const defaultFormat = (n) => Math.round(n).toLocaleString('en-IN');

/**
 * Counts from 0 to `to` once when it scrolls into view.
 * Props: to, duration (s), prefix, suffix, format, sx.
 */
function AnimatedCounter({ to, duration, prefix, suffix, format, sx }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [value, setValue] = useState(0);
  const fmt = format || defaultFormat;

  useEffect(() => {
    if (!isInView || to === 0) {
      setValue(to);
      return;
    }

    let startTime = null;
    let raf;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(eased * to);
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      }
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [isInView, to, duration]);

  return (
    <Box component="span" ref={ref} sx={sx}>
      {prefix}
      {fmt(value)}
      {suffix}
    </Box>
  );
}

AnimatedCounter.propTypes = {
  to: PropTypes.number.isRequired,
  duration: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  format: PropTypes.func,
  sx: PropTypes.object,
};

AnimatedCounter.defaultProps = {
  duration: 1.8,
  prefix: '',
  suffix: '',
  format: null,
  sx: {},
};

export default AnimatedCounter;
